/* eslint-disable import/no-duplicates */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import en from 'date-fns/locale/en-US';
import fr from 'date-fns/locale/fr-CA';
import de from 'date-fns/locale/de';
import pt from 'date-fns/locale/pt-BR';
import es from 'date-fns/locale/es';

import { update as updateFn, retrieve as retrieveFn } from '../../services/session-service';
import {
  getDefaultLanguage,
  setLocale as setLocaleFn,
  ISO_LOCALE_MAP,
} from '../../services/translation/api';

import Context from './context';

const ISO_DATE_LOCALE = {
  [ISO_LOCALE_MAP.en]: en,
  [ISO_LOCALE_MAP.fr_CA]: fr,
  [ISO_LOCALE_MAP.pt_BR]: pt,
  [ISO_LOCALE_MAP.de]: de,
  [ISO_LOCALE_MAP.es]: es,
};

interface Props {
  children: React.ReactNode;
}

/**
 * A context provider for the translation (language and locale). You can access
 * the context with common/hooks/useTranslation.
 */
const TranslationProvider = ({ children }: Props) => {
  const defaultLanguage = getDefaultLanguage();

  const [stored, setStored] = useState({
    language: defaultLanguage,
    locale: ISO_LOCALE_MAP[defaultLanguage],
    dateLocale: ISO_DATE_LOCALE[defaultLanguage],
  });

  const updateStored = useCallback(async (language: string) => {
    await setLocaleFn(language);
    setStored({
      language,
      locale: ISO_LOCALE_MAP[language],
      dateLocale: ISO_DATE_LOCALE[ISO_LOCALE_MAP[language]],
    });
  }, []);

  useEffect(() => {
    async function effect() {
      const language = (await retrieveFn())?.language || getDefaultLanguage();
      await updateStored(language);
    }
    effect();
  }, [updateStored]);

  const setLanguage = useCallback(
    async language => {
      await Promise.all([updateStored(language), updateFn({ language })]);
    },
    [updateStored],
  );

  const value = useMemo(() => ({ ...stored, setLanguage }), [stored, setLanguage]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export default TranslationProvider;
